// @wander/shared — Modification type detection
// Ported from web's src/contexts/AuthContext.js

// Patterns that look complex but are actually basic item-level changes
// These take priority and mark the modification as BASIC
const BASIC_EXCLUSION_PATTERNS = [
  /cheaper\s+(restaurant|hotel|activity|option|place|spot)/,
  /budget[- ]?friendly\s+(restaurant|hotel|option)/,
  /longer\s+(lunch|dinner|breakfast|break|meal)/,
  /extend\s+(the\s+)?(activity|visit|tour|stay at)/,
  /group\s+(the\s+)?(activities|items|events)/,
  /join\s+(them|these|the activities)/,
  /split\s+(the\s+)?day/,
  /budget\s+(option|restaurant|hotel|activity)/,
];

// Complex modification patterns — require Planner tier
const COMPLEX_MODIFICATION_PATTERNS = [
  // Trip extension
  /\b(add|extra|more)\s*\d*\s*days?\b/i,
  /\bextend\s+(the\s+)?(trip|itinerary|vacation)\b/i,
  /\bmake\s+(the\s+)?(trip|it)\s+(longer|extended)\b/i,
  // City/destination additions
  /\badd\s+(a\s+)?(city|destination|stop)\b/i,
  /\b(side|day)\s*trip\s+to\b/i,
  /\binclude\s+(a\s+)?(visit|stop)\s+(to|at|in)\b/i,
  // Full replanning
  /\b(replan|redo|rebuild)\s+(the\s+)?(entire|whole|full)?\s*(trip|itinerary)\b/i,
  /\bstart\s+over\b/i,
  /\bchange\s+everything\b/i,
  // Points/miles optimization
  /\b(optimize|use)\s+(for\s+)?(points|miles|rewards)\b/i,
  /\bcredit\s+card\s+(points|rewards)\b/i,
  // Trip splitting/merging
  /\bsplit\s+(the\s+)?(trip|itinerary)\s+(into|in)\b/i,
  /\bdivide\s+(the\s+)?(trip|itinerary)\b/i,
  /\bseparate\s+trips?\b/i,
  /\b(combine|merge)\s+(the\s+)?(trips?|itineraries)\b/i,
  // Budget reoptimization (whole trip)
  /\bmake\s+(the\s+)?(whole|entire)?\s*(trip|itinerary)\s+(cheaper|less expensive)\b/i,
  /\bsave\s+money\s+on\s+(the\s+)?(trip|overall)\b/i,
  /\breoptimize\s+(for\s+)?budget\b/i,
  // Group coordination
  /\bcoordinate\s+(with|for)\s+(the\s+)?(group|everyone)\b/i,
  /\b(everyone|all of us)\s+(wants?|needs?)\b/i,
  /\bgroup\s+(planning|coordination|schedule)\b/i,
];

/**
 * Detect whether a modification message is 'basic' or 'complex'.
 * Basic = item-level swaps/adjustments (all tiers).
 * Complex = trip-level structural changes (Planner+ tier).
 *
 * @param {string} message — user's modification request
 * @returns {'basic' | 'complex'}
 */
export const detectModificationType = (message) => {
  if (!message) return 'basic';
  const lower = message.toLowerCase();

  // First check basic exclusion patterns (look complex but are basic)
  for (const pattern of BASIC_EXCLUSION_PATTERNS) {
    if (pattern.test(lower)) {
      return 'basic';
    }
  }

  // Check complex patterns
  for (const pattern of COMPLEX_MODIFICATION_PATTERNS) {
    if (pattern.test(lower)) {
      return 'complex';
    }
  }

  return 'basic';
};

/**
 * Classify a modification message into a specific action type.
 *
 * @param {string} message — user's modification request
 * @returns {string} — action type (e.g., 'swap_item', 'add_days', 'adjust_time')
 */
export const classifyModificationAction = (message) => {
  const lower = message.toLowerCase();

  // Basic item-level (even if they contain "complex" words)
  if (lower.match(/cheaper\s+(restaurant|hotel|activity|option)/)) return 'swap_item';
  if (lower.match(/budget[- ]?friendly\s+(restaurant|hotel)/)) return 'swap_item';
  if (lower.match(/longer\s+(lunch|dinner|breakfast|break)/)) return 'adjust_time';
  if (lower.match(/extend\s+(the\s+)?(activity|visit|tour)/)) return 'adjust_time';
  if (lower.match(/split\s+(the\s+)?day/)) return 'reorder_day';
  if (lower.match(/group\s+(the\s+)?(activities|items)/)) return 'reorder_day';

  // Complex (trip-level changes)
  if (lower.match(/\b(add|extra|more)\s*\d*\s*days?\b/)) return 'add_days';
  if (lower.match(/\bextend\s+(the\s+)?(trip|itinerary)\b/)) return 'add_days';
  if (lower.match(/\badd\s+(a\s+)?(city|destination|stop)\b/)) return 'add_city';
  if (lower.match(/\b(side|day)\s*trip\s+to\b/)) return 'add_city';
  if (lower.match(/\b(replan|redo)\s+(the\s+)?(entire|whole)?\s*(trip|itinerary)\b/)) return 'multi_city_replan';
  if (lower.match(/\bstart\s+over\b/)) return 'multi_city_replan';
  if (lower.match(/\bchange\s+everything\b/)) return 'multi_city_replan';
  if (lower.match(/\b(optimize|use)\s+(for\s+)?(points|miles|rewards)\b/)) return 'optimize_for_points';
  if (lower.match(/\bcredit\s+card\s+(points|rewards)\b/)) return 'optimize_for_points';
  if (lower.match(/\bsplit\s+(the\s+)?(trip|itinerary)\b/)) return 'split_trip';
  if (lower.match(/\bdivide\s+(the\s+)?(trip|itinerary)\b/)) return 'split_trip';
  if (lower.match(/\b(combine|merge)\s+(the\s+)?(trips?|itineraries)\b/)) return 'merge_itineraries';
  if (lower.match(/\bmake\s+(the\s+)?(trip|itinerary)\s+(cheaper|less expensive)\b/)) return 'budget_reoptimize';
  if (lower.match(/\bsave\s+money\s+on\s+(the\s+)?trip\b/)) return 'budget_reoptimize';
  if (lower.match(/\bcoordinate\s+(with|for)\s+(the\s+)?group\b/)) return 'group_coordinate';
  if (lower.match(/\b(everyone|all of us)\s+(wants?|needs?)\b/)) return 'group_coordinate';

  // Basic actions (item-level)
  if (lower.match(/swap|replace|different\s+(restaurant|activity|hotel)/)) return 'swap_item';
  if (lower.match(/move|adjust|earlier|later|\d+\s*(am|pm)/)) return 'adjust_time';
  if (lower.match(/remove|delete|skip/)) return 'remove_item';
  if (lower.match(/reorder|before|after/)) return 'reorder_day';
  if (lower.match(/transit|transport|drive|walk|uber|taxi/)) return 'change_transport_mode';

  return 'general_modification';
};
